import { FunctionComponent, PropsWithChildren, useContext, useEffect, useState } from 'react';

import { AnalyticsEvent, PostInteractionEventDimensions, logAnalyticsEvent } from '@vestico/utils';

import { WidgetContentEntry } from '@vestico/model';

import { UserContext } from '@vestico/shared';

import { VisitsAPIContext, WidgetAuthContext } from '../../MainContext';
import { ContentGlobalsContext } from '../../WidgetEventsManager';
import { ConfigContext } from '../../WidgetEventsManager';
import { userDataCompletedForAnyProduct } from '../../model/helpers';
import { ContentProductContext, ContentResponsesContext } from './ContentDisplayContext';

export interface ContentDisplayEventsProps extends PropsWithChildren {
  entry: WidgetContentEntry;
}

export const ContentDisplayEvents: FunctionComponent<ContentDisplayEventsProps> = ({ entry, children }) => {
  //
  //  OPEN STATE MGMT //
  //

  const { id } = entry;
  const { contentIsOpen, entryIdentifier } = useContext(ContentGlobalsContext);
  const { responses, selectedResponse } = useContext(ContentResponsesContext);
  const { products } = useContext(ContentProductContext);

  const { user } = useContext(UserContext);

  const { authInfo, resetAuth } = useContext(WidgetAuthContext);

  //
  //  FETCH LOGIC //
  //

  const { clientId } = useContext(ConfigContext);

  const visitsAPI = useContext(VisitsAPIContext);

  const [didReportOpenEvent, setDidReportOpenEvent] = useState(false);
  const [didReportMatchEvent, setDidReportMatchEvent] = useState(false);

  useEffect(() => {
    if (!contentIsOpen) {
      setDidReportOpenEvent(false);
    }
  }, [contentIsOpen]);

  // Record open
  useEffect(() => {
    if (
      !didReportOpenEvent &&
      contentIsOpen &&
      products &&
      visitsAPI &&
      user &&
      user.uid &&
      authInfo &&
      responses &&
      selectedResponse &&
      id === entryIdentifier
    ) {
      console.debug('record content widget open');
      setDidReportOpenEvent(true);
      visitsAPI.recordVisitEvent('showcaseOpen', products, user.uid, { authInfo: authInfo, resetAuth });

      for (const product of products) {
        const { documentID, name, brandName } = product;
        const dimensions: PostInteractionEventDimensions = {
          item_id: documentID,
          name: name,
          brand: brandName,
          provider: clientId,
          entry_id: id,
          post_id: selectedResponse?.postDocumentID,
          post_media_type: selectedResponse?.media.mainMimeType,
          index: responses.indexOf(selectedResponse),
          last_post_index: responses.length - 1,
        };
        logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.open, dimensions });
      }
    }
  }, [
    contentIsOpen,
    products,
    visitsAPI,
    user,
    authInfo,
    selectedResponse,
    resetAuth,
    clientId,
    entry.id,
    didReportOpenEvent,
    responses,
    id,
    entryIdentifier,
  ]);

  // Record match request
  useEffect(() => {
    if (
      !didReportMatchEvent &&
      contentIsOpen &&
      products &&
      visitsAPI &&
      user &&
      user.uid &&
      authInfo &&
      id === entryIdentifier &&
      userDataCompletedForAnyProduct(products, user)
    ) {
      visitsAPI.recordVisitEvent('showcaseMatchRequest', products, user.uid, { authInfo: authInfo, resetAuth });
      setDidReportMatchEvent(true);
    }
  }, [contentIsOpen, products, didReportMatchEvent, visitsAPI, user, authInfo, resetAuth, id, entryIdentifier]);

  // Record when selected response has changed
  const [lastVisitedPostDocID, setLastVisitedPostDocID] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (
      responses &&
      responses.length > 0 &&
      selectedResponse &&
      products &&
      user &&
      user.uid &&
      authInfo &&
      visitsAPI &&
      contentIsOpen &&
      entryIdentifier === id
    ) {
      const { postDocumentID } = selectedResponse;
      if (postDocumentID === lastVisitedPostDocID) {
        console.debug('not recording same content visit product twice');
        return;
      }

      // check if products are the same as the response's
      const responseProductDocIDs = new Set(selectedResponse.products.map((p) => p.documentID));
      const missingProducts = products.filter((p) => !responseProductDocIDs.has(p.documentID));

      if (products.length > 0 && missingProducts.length > 0) {
        console.debug('not recording visit because products do not match');
        return;
      }

      setLastVisitedPostDocID(postDocumentID);

      // Record visit
      console.debug(`record content visit product: ${postDocumentID}, product: ${products.map((p) => p.documentID).join(',')}`);
      visitsAPI.recordVisitEvent('showcaseProductVisit', products, user.uid, { authInfo: authInfo, resetAuth });

      for (const product of products) {
        const { documentID, name, brandName } = product;
        const dimensions: PostInteractionEventDimensions = {
          item_id: documentID,
          name: name,
          brand: brandName,
          provider: clientId,
          entry_id: id,
          post_id: selectedResponse?.postDocumentID,
          post_media_type: selectedResponse?.media.mainMimeType,
          index: responses.indexOf(selectedResponse),
          last_post_index: responses.length - 1,
        };
        logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.goToPost, dimensions });
      }
    }
  }, [
    selectedResponse,
    products,
    responses,
    user,
    authInfo,
    visitsAPI,
    contentIsOpen,
    lastVisitedPostDocID,
    resetAuth,
    clientId,
    id,
    entryIdentifier,
  ]);

  return <>{children}</>;
};
